import { useEffect, useRef, useState } from "react";
import { useTranslation } from "../i18n";
import { motion, AnimatePresence } from "framer-motion";
import JobImageCard from "./cards/JobImageCard";
import JobModal from "./JobModal";
import {
  RiStackFill,
  RiStackLine,
  RiCheckboxCircleLine,
  RiCloseLine,
  RiTranslate2,
  RiRefreshLine,
} from "react-icons/ri";
import type { Job } from "../types/job";
import { isQuestionBatchType } from "../lib/questionBatch";
import {
  sortByCreatedAtDesc,
  partitionByAge,
  partitionByApproval,
  mergeIntoHistory,
} from "../lib/queueOrdering";

interface JobWidgetProps {
  jobs: Job[];
  historyJobs: Job[];
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  openSignal?: number;
  hideFab?: boolean;
  onApprove: (jobId: Job) => Promise<void>;
  onDecline: (jobId: string) => Promise<void>;
  onDismiss: (jobId: string) => Promise<void>;
  onResetMockJobs?: () => void;
}

export default function JobWidget({
  jobs,
  historyJobs,
  onApprove,
  onDecline,
  onDismiss,
  isOpen,
  setIsOpen,
  openSignal,
  hideFab = false,
  onResetMockJobs,
}: JobWidgetProps) {
  const { t, i18n } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [activeTab, setActiveTab] = useState<"queue" | "history">("queue");
  const [categoryFilter, setCategoryFilter] = useState<
    "all" | "image" | "avatar" | "video" | "other"
  >("all");

  const getJobCategory = (type: string): "image" | "avatar" | "video" | "other" => {
    const k = (type || "").toLowerCase().replace(/_/g, "-");
    if (k === "video-generation" || k === "staged-video-init" || k === "staged-video-generation")
      return "video";
    if (
      k === "ai-avatar" ||
      k === "tts-avatar" ||
      k === "tavus-video" ||
      k === "lipsync" ||
      k === "azure-avatar" ||
      k === "image-to-avatar" ||
      k === "image-to-avatar-generation" ||
      k === "talking-avatar"
    )
      return "avatar";
    if (
      k === "image-generation" ||
      k === "image-edit" ||
      k === "image-editing" ||
      k === "dt" ||
      k === "merlin-image-animator" ||
      k === "image-animation" ||
      k === "text-to-image" ||
      k === "infographic-scene-generate" ||
      k === "infographic-images-generate" ||
      k === "infographic-image-generate"
    )
      return "image";
    return "other";
  };

  const matchesCategory = (job: Job) =>
    categoryFilter === "all" || getJobCategory(job.type) === categoryFilter;

  const isHiddenType = (type: string) => {
    const k = (type || "")
      .toLowerCase()
      .trim()
      .replace(/[\s_]+/g, "-");
    return k === "course-generation" || k === "preview-image";
  };

  const isTtsType = (type: string) =>
    (type || "")
      .toLowerCase()
      .trim()
      .replace(/[\s_]+/g, "-") === "text-to-speech";
  const isNarrationType = (type: string) =>
    (type || "")
      .toLowerCase()
      .trim()
      .replace(/[\s_]+/g, "-") === "training-narration";
  const isFinishedType = (type: string) => isTtsType(type) || isNarrationType(type);
  const isAudioUrl = (url?: string | null) => !!url && /\.(mp3|wav|m4a|aac|oga)(\?|#|$)/i.test(url);

  // Aynı seslendirme için backend birden çok kayıt üretiyor:
  //  - sahneye bağlı kayıt (navId/sceneIndex var, resultUrl yok)
  //  - üretilen ses kaydı (resultUrl mp3 var, navId/sceneIndex yok)
  // Bunları tek karta indirip, sahne kaydına EN GÜNCEL (silinmemiş) sesi ekliyoruz.
  const collapseTts = (list: Job[]): Job[] => {
    const ts = (j: Job) => new Date(j.updatedAt || j.createdAt || 0).getTime();
    const groups = new Map<string, Job[]>();
    for (const job of list) {
      if (!isTtsType(job.type)) continue;
      const key = `${job.trainingId || ""}::${(job.prompt || "").trim()}`;
      const arr = groups.get(key) || [];
      arr.push(job);
      groups.set(key, arr);
    }
    const emitted = new Set<string>();
    const out: Job[] = [];
    for (const job of list) {
      if (!isTtsType(job.type)) {
        out.push(job);
        continue;
      }
      const key = `${job.trainingId || ""}::${(job.prompt || "").trim()}`;
      if (emitted.has(key)) continue;
      emitted.add(key);
      const arr = groups.get(key)!;

      // Görünüm/onay için sahneye bağlı (silinmemiş) kaydı tercih et
      const primary =
        arr.find((j) => (j.navId || j.sceneIndex) && !j.deletedAt) ||
        arr.find((j) => j.navId || j.sceneIndex) ||
        arr.find((j) => !j.deletedAt) ||
        arr[0];

      // En güncel (silinmemiş) ses kaydını seç — eski/silinen sesleri eler
      const newestAudio = arr
        .filter((j) => !j.deletedAt && isAudioUrl(j.resultUrl))
        .sort((a, b) => ts(b) - ts(a))[0];

      out.push(
        newestAudio && newestAudio.resultUrl !== primary.resultUrl
          ? { ...primary, resultUrl: newestAudio.resultUrl }
          : primary
      );
    }
    return out;
  };

  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef<HTMLDivElement>(null);
  const widgetRootRef = useRef<HTMLDivElement>(null);

  const changeLanguage = (lng: "tr" | "en" | "es") => {
    i18n.changeLanguage(lng);
    try {
      localStorage.setItem("widget_lang", lng);
    } catch {}
    setIsLangMenuOpen(false);
  };

  useEffect(() => {
    if (!isLangMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setIsLangMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isLangMenuOpen]);

  useEffect(() => {
    if (!isFilterMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target as Node)) {
        setIsFilterMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isFilterMenuOpen]);

  // Click-outside-to-dismiss for the open tray (formerly a full-viewport
  // backdrop div in App.tsx — removed with the iframe-era 100vh wrapper).
  // Same mousedown+contains pattern as the menus above.
  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (widgetRootRef.current && !widgetRootRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isOpen]);

  const filterOptions = [
    { key: "all", label: t("widget.filter.all") },
    { key: "image", label: t("widget.filter.image") },
    { key: "avatar", label: t("widget.filter.avatar") },
    { key: "video", label: t("widget.filter.video") },
    { key: "other", label: t("widget.filter.other") },
  ] as const;
  const activeFilterLabel = filterOptions.find((o) => o.key === categoryFilter)?.label;

  // ponytail: 5-minute poll to nudge the 24h queue→history archive boundary
  // while the tray sits open; upgrade to per-item setTimeout scheduling if
  // sub-5-minute precision is ever needed.
  const [archiveTick, setArchiveTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setArchiveTick(Date.now()), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // ✅ Sadece approve edilmemiş job'ları göster (course_generation hariç), en yeni en üstte
  const typeFilteredJobs = sortByCreatedAtDesc(collapseTts(jobs))
    .filter((job) => !isHiddenType(job.type))
    .filter(matchesCategory);
  // Widget tepsisi DIŞINDA onaylanmış job'lar (ör. Merlin animatör popup'ının
  // approveJob çağrısı) Kuyruk'ta değil Geçmiş'te görünmeli — 24h auto-archive
  // ile aynı görsel taşıma yolu, sunucu durumu değişmez.
  const { active: pendingJobsAll, archived: approvedJobs } = partitionByApproval(typeFilteredJobs);
  // 24 saatten eski kuyruk öğeleri Geçmiş sekmesine (görsel olarak) taşınır — sunucu durumu değişmez.
  const { active: pendingJobs, archived: autoArchivedJobs } = partitionByAge(
    pendingJobsAll,
    archiveTick
  );
  // Bitmiş işler: seslendirme (text-to-speech) ve sahne anlatımı (training-narration)
  // önce "İşleniyor"da bekler, tamamlanınca (inspect/completed) "Bitmiş İşler"e geçer.
  const finishedJobs = pendingJobs.filter(
    (job) => isFinishedType(job.type) && (job.status === "inspect" || job.status === "completed")
  );
  // Soru oluşturma bitince (inspect/completed) "Onay Bekliyor" grubunda gösterilir
  const waitingApprovalJobs = pendingJobs.filter(
    (job) =>
      (isQuestionBatchType(job) && (job.status === "inspect" || job.status === "completed")) ||
      (job.status === "inspect" && !isFinishedType(job.type))
  );
  const processingJobs = pendingJobs.filter(
    (job) => job.status === "processing" || job.status === "pending"
  );
  const failedJobs = pendingJobs.filter((job) => job.status === "failed");
  const fetchedHistoryJobs = sortByCreatedAtDesc(collapseTts(historyJobs))
    .filter((job) => !isHiddenType(job.type))
    .filter(matchesCategory);
  const filteredHistoryJobs = mergeIntoHistory(fetchedHistoryJobs, [
    ...autoArchivedJobs,
    ...approvedJobs,
  ]);
  const queueCount = pendingJobs.length;
  const historyCount = filteredHistoryJobs.length;

  useEffect(() => {
    if (openSignal) setIsOpen(true);
  }, [openSignal]);

  const handleJobClick = (job: Job) => {
    setSelectedJob(job);
    setIsModalOpen(true);
  };

  return (
    <div ref={widgetRootRef} className="fixed bottom-4 right-4 z-50 pointer-events-auto">
      {!hideFab && (
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.92 }}
          animate={{
            boxShadow: isOpen
              ? "0px 0px 40px rgba(168, 85, 247, 0.65)"
              : "0px 10px 25px rgba(0,0,0,0.25)",
          }}
          transition={{ type: "spring", stiffness: 400, damping: 18 }}
          className="relative rounded-full w-12 h-12 bg-gradient-to-br from-purple-500 to-fuchsia-500 shadow-lg cursor-pointer"
        >
          <motion.span
            className="absolute inset-0 rounded-full blur-md opacity-70"
            animate={{
              scale: isOpen ? [1, 1.25, 1] : 1,
              opacity: isOpen ? [0.4, 0.8, 0.4] : 0,
            }}
            transition={{
              duration: 1.4,
              repeat: isOpen ? Infinity : 0,
              ease: "easeInOut",
            }}
            style={{
              background:
                "radial-gradient(circle at 30% 30%, rgba(168,85,247,0.9), transparent 60%)",
            }}
          />

          <AnimatePresence>
            <motion.div
              key={isOpen ? "open" : "closed"}
              initial={{ opacity: 0, scale: 0.75 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.75 }}
              transition={{ type: "spring", stiffness: 600, damping: 26 }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            >
              {isOpen ? (
                <RiStackFill className="text-white w-6 h-6" />
              ) : (
                <RiStackLine className="text-white w-6 h-6" />
              )}
            </motion.div>
          </AnimatePresence>
        </motion.button>
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="popup"
            initial={{ opacity: 0, scale: 0.9, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
            className="absolute bottom-14 right-8 w-[370px] h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="absolute inset-0   pointer-events-none" />

            <div className="relative h-full flex flex-col">
              <div className="px-4 pt-3 pb-2 mb-1 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">{t("widget.activity")}</p>
                <div className="flex items-center gap-1">
                  {onResetMockJobs && (
                    <button
                      onClick={onResetMockJobs}
                      className="flex items-center gap-1 px-2 py-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 active:scale-[0.98] transition-all duration-150 ease-out"
                      aria-label="Reset mock data"
                      title="Reset mock data"
                    >
                      <RiRefreshLine className="w-4 h-4" />
                    </button>
                  )}
                  <div className="relative" ref={langMenuRef}>
                    <button
                      onClick={() => setIsLangMenuOpen((v) => !v)}
                      className="flex items-center gap-1 px-2 py-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 active:scale-[0.98] transition-all duration-150 ease-out"
                      aria-label="Language"
                    >
                      <RiTranslate2 className="w-4 h-4" />
                      <span className="text-[11px] font-semibold uppercase">
                        {i18n.language.toUpperCase()}
                      </span>
                    </button>
                    <AnimatePresence>
                      {isLangMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.12 }}
                          className="absolute right-0 mt-1 w-28 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-10"
                        >
                          <button
                            onClick={() => changeLanguage("tr")}
                            className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition ${i18n.language === "tr" ? "text-purple-700 font-semibold" : "text-gray-700"}`}
                          >
                            Türkçe
                          </button>
                          <button
                            onClick={() => changeLanguage("en")}
                            className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition ${i18n.language === "en" ? "text-purple-700 font-semibold" : "text-gray-700"}`}
                          >
                            English
                          </button>
                          <button
                            onClick={() => changeLanguage("es")}
                            className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition ${i18n.language === "es" ? "text-purple-700 font-semibold" : "text-gray-700"}`}
                          >
                            Español
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 active:scale-[0.98] transition-all duration-150 ease-out"
                    aria-label={t("widget.close")}
                  >
                    <RiCloseLine className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex items-center gap-6 px-4 border-b border-gray-200/70">
                <button
                  onClick={() => setActiveTab("queue")}
                  className={`relative pb-2 flex items-center gap-2 text-[11px] active:scale-[0.98] transition-all duration-150 ease-out ${
                    activeTab === "queue"
                      ? "font-semibold text-purple-700"
                      : "font-medium text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {t("widget.jobQueue")}
                  <span
                    className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${
                      activeTab === "queue"
                        ? "bg-purple-100 text-purple-700"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {queueCount}
                  </span>
                  {activeTab === "queue" && (
                    <span className="absolute -bottom-[1px] left-0 right-0 h-[2px] bg-purple-600 rounded-full" />
                  )}
                </button>
                <button
                  onClick={() => setActiveTab("history")}
                  className={`relative pb-2 flex items-center gap-2 text-[11px] active:scale-[0.98] transition-all duration-150 ease-out ${
                    activeTab === "history"
                      ? "font-semibold text-purple-700"
                      : "font-medium text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {t("widget.history")}
                  <span
                    className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${
                      activeTab === "history"
                        ? "bg-purple-100 text-purple-700"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {historyCount}
                  </span>
                  {activeTab === "history" && (
                    <span className="absolute -bottom-[1px] left-0 right-0 h-[2px] bg-purple-600 rounded-full" />
                  )}
                </button>

                {/* Category filter dropdown */}
                <div className="relative ml-auto pb-1.5" ref={filterMenuRef}>
                  <button
                    onClick={() => setIsFilterMenuOpen((v) => !v)}
                    className={`flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md border active:scale-[0.98] transition-all duration-150 ease-out ${
                      categoryFilter !== "all"
                        ? "bg-purple-50 text-purple-700 border-purple-200"
                        : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                    }`}
                    aria-label="Filter"
                  >
                    <span>{activeFilterLabel}</span>
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M3 4.5L6 7.5L9 4.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <AnimatePresence>
                    {isFilterMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.12 }}
                        className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-10"
                      >
                        {filterOptions.map((opt) => (
                          <button
                            key={opt.key}
                            onClick={() => {
                              setCategoryFilter(opt.key);
                              setIsFilterMenuOpen(false);
                            }}
                            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 transition ${
                              categoryFilter === opt.key
                                ? "text-purple-700 font-semibold"
                                : "text-gray-700"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-hidden">
                {activeTab === "queue" ? (
                  <div className="p-4 h-full flex flex-col">
                    <div className="flex-1 space-y-4 overflow-y-auto">
                      {pendingJobs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400">
                          <RiStackLine className="w-12 h-12 mb-2 opacity-30" />
                          <p className="text-xs">{t("widget.allJobsCompleted")}</p>
                        </div>
                      ) : (
                        <>
                          {waitingApprovalJobs.length > 0 && (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-[10px] font-semibold text-gray-500 uppercase tracking-[0.08em]">
                                <span>{t("widget.waitingApproval")}</span>
                                <span className="flex-1 h-px bg-gray-200" />
                              </div>
                              {waitingApprovalJobs.map((job) => {
                                // Seslendirme (text-to-speech): onayla/reddet yok, sadece tıklayıp dinle
                                const isTts = isTtsType(job.type);
                                const hideDecline =
                                  job.type === "training-aspect-conversion" ||
                                  job.type === "training_aspect_conversion" ||
                                  job.type === "subtitle-generation" ||
                                  job.type === "subtitle_generation" ||
                                  job.type === "training-video-export" ||
                                  job.type === "training_video_export";
                                return (
                                  <JobImageCard
                                    job={job}
                                    key={job.id}
                                    statusLabel={t("widget.waitingApproval")}
                                    onApproveClick={isTts ? undefined : () => onApprove(job)}
                                    onDeclineClick={
                                      isTts || hideDecline ? undefined : () => onDecline(job.id)
                                    }
                                    onDismissClick={() => onDismiss(job.id)}
                                    approveLabel={t("actions.approve")}
                                    declineLabel={t("actions.decline")}
                                    onClick={() => handleJobClick(job)}
                                  />
                                );
                              })}
                            </div>
                          )}

                          {finishedJobs.length > 0 && (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-[10px] font-semibold text-gray-500 uppercase tracking-[0.08em]">
                                <span>{t("widget.finished")}</span>
                                <span className="flex-1 h-px bg-gray-200" />
                              </div>
                              {finishedJobs.map((job) => (
                                <JobImageCard
                                  job={job}
                                  key={job.id}
                                  statusLabel={t("status.completed")}
                                  onApproveClick={() => onApprove(job)}
                                  onDismissClick={() => onDismiss(job.id)}
                                  approveLabel={t("actions.moveToHistory")}
                                  onClick={() => handleJobClick(job)}
                                />
                              ))}
                            </div>
                          )}

                          {processingJobs.length > 0 && (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-[10px] font-semibold text-gray-500 uppercase tracking-[0.08em]">
                                <span>{t("widget.processing")}</span>
                                <span className="flex-1 h-px bg-gray-200" />
                              </div>
                              {processingJobs.map((job) => (
                                <JobImageCard
                                  job={job}
                                  key={job.id}
                                  statusLabel={t("widget.processing")}
                                  onDismissClick={() => onDismiss(job.id)}
                                  onClick={() => handleJobClick(job)}
                                />
                              ))}
                            </div>
                          )}

                          {failedJobs.length > 0 && (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-[10px] font-semibold text-red-400 uppercase tracking-[0.08em]">
                                <span>{t("widget.failed")}</span>
                                <span className="flex-1 h-px bg-red-100" />
                              </div>
                              {failedJobs.map((job) => (
                                <JobImageCard
                                  job={job}
                                  key={job.id}
                                  statusLabel={t("widget.failed")}
                                  onDeclineClick={() => onDecline(job.id)}
                                  onDismissClick={() => onDismiss(job.id)}
                                  declineLabel={t("actions.decline")}
                                  onClick={() => handleJobClick(job)}
                                />
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 h-full flex flex-col">
                    <div className="mb-3">
                      <p className="text-xs text-gray-600">
                        {filteredHistoryJobs.length === 0
                          ? t("widget.noHistoryYet")
                          : t("widget.jobsTotal", { count: filteredHistoryJobs.length })}
                      </p>
                    </div>

                    <div className="flex-1 space-y-2 overflow-y-auto">
                      {filteredHistoryJobs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-400">
                          <RiCheckboxCircleLine className="w-12 h-12 mb-2 opacity-30" />
                          <p className="text-xs">{t("widget.noHistoryYet")}</p>
                        </div>
                      ) : (
                        filteredHistoryJobs.map((job) => {
                          const hideRecall =
                            isTtsType(job.type) ||
                            job.type === "training-aspect-conversion" ||
                            job.type === "training_aspect_conversion" ||
                            job.type === "subtitle-generation" ||
                            job.type === "subtitle_generation" ||
                            job.type === "training-video-export" ||
                            job.type === "training_video_export";
                          return (
                            <JobImageCard
                              job={job}
                              key={job.id}
                              statusLabel={t("status.inspect")}
                              showHistory={true}
                              onApproveClick={hideRecall ? undefined : () => onApprove(job)}
                              onDismissClick={() => onDismiss(job.id)}
                              approveLabel={t("actions.recall")}
                              onClick={() => handleJobClick(job)}
                            />
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {selectedJob && (
        <JobModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          job={selectedJob}
          onApprove={onApprove}
          onDecline={onDecline}
          onDismiss={onDismiss}
          showHistory={activeTab === "history"}
        />
      )}
    </div>
  );
}
